/**
 * The TSR Findings Walkthrough: the house "here is what we found" page.
 *
 * A preset Custom Section, for the same reason the TSR Dashboard is one --
 * every band stays editable, removable and reorderable, and anything else can
 * be dropped in beside them. The one piece that could not be assembled from
 * existing components is the findings table itself, so that is a real
 * component type (`findings-walkthrough`) with its own settings panel and its
 * own renderer on both the DOCX and PDF paths.
 *
 * Why not reuse findings-summary: that table is an index. It answers "how many
 * and how bad" in a row per finding, and it is read by scanning down the
 * severity column. This page answers "what actually happened" -- the finding's
 * own identifier, its title, and a line of plain description underneath -- for
 * a reader who will not open the detailed findings section at all.
 *
 * The same snapshot trade-off applies as to the Dashboard: improving this
 * preset does not reach walkthroughs already inserted into templates. That is
 * the price of every band staying editable, and it is the right way round.
 */
import { nextStepsDefaultConfig } from './nextStepsDefaults';

const NAVY = '#1f3864';
const CAPTION = '#2e86c1';
const INK = '#1f3864';
const MUTED = '#5f6b7a';
const BORDER = '#d8dfe8';
const STATUS_RED = '#c00000';

/** Small blue band captions, matching the house dashboard. */
const caption = (text) => ({
  type: 'heading',
  config: { text, level: 3, align: 'left', color: CAPTION },
});

export function tsrFindingsWalkthroughConfig() {
  return {
    heading: '',
    components: [
      caption('Findings walkthrough'),
      {
        type: 'findings-walkthrough',
        config: {
          heading: '',
          // The identifier is a column of its own. It used to be printed
          // inside the title cell with a separator between the two, which
          // made the pair unsortable, unscannable and impossible to quote
          // without the title coming along with it.
          columns: ['uid', 'finding', 'severity', 'cvss', 'status'],
          labelUid: 'Finding ID', labelFinding: 'Title', labelSeverity: 'Severity',
          labelCvss: 'CVSS', labelStatus: 'Status',
          uidColor: INK, uidSize: 9, uidWidth: 1700,

          headerBg: NAVY, headerColor: '#ffffff', headerSize: 9, headerBold: true,
          borderColor: BORDER,

          // Off, because the identifier has a column now. Kept as a written
          // key rather than deleted so the config reads the way it behaves.
          showUid: false, uidSeparator: '  |  ',
          titleColor: INK, titleSize: 9,

          // Details rather than observation: the description here is "what the
          // weakness is", which is what a reader skimming the page needs.
          // Observation is "where we saw it", and belongs with the evidence.
          showDescription: true, descriptionField: 'details',
          descriptionColor: MUTED, descriptionSize: 8,

          // A filled block, because the column is read at a glance down the
          // page and coloured text at eight point does not carry that far.
          severityStyle: 'block', severityTextColor: '#ffffff',
          severitySize: 8, severityUpper: true,

          cvssColor: INK, cvssSize: 9,
          statusColor: STATUS_RED, statusSize: 9, statusBold: true,

          // Every severity, every finding. A walkthrough that quietly dropped
          // the Informational rows would be a different document from the one
          // the findings table reports.
          severityFilter: [], maxRows: 0,

          // Next steps now belongs to the widget rather than sitting beside it
          // as a caption and a custom table. It was two separate bands here
          // until the bare Findings Walkthrough -- the one in the Components
          // list -- turned out to print the table and nothing else, so "what
          // do I do with this" only existed if you happened to add the house
          // preset. Every string, colour and size is still editable, in the
          // widget's own settings panel.
          nextSteps: nextStepsDefaultConfig(),
        },
      },
    ],
  };
}

export default tsrFindingsWalkthroughConfig;
